import { RefObject, useEffect } from 'react';

interface UseScrollIntoViewWithHeaderOptions {
  /**
   * The element ref to scroll into view
   */
  elementRef?: RefObject<HTMLElement> | null;
  /**
   * The element to scroll into view (alternative to ref)
   */
  element?: HTMLElement | null;
  /**
   * Condition to trigger the scroll
   */
  shouldScroll: boolean;
  /**
   * Height of the fixed header in pixels (default: 64px for mt-16)
   */
  headerHeight?: number;
  /**
   * Padding to add when scrolling (default: 10px)
   */
  padding?: number;
  /**
   * Selector for the main content container
   * (default: '.h-screen.overflow-y-auto')
   */
  containerSelector?: string;
}

/**
 * Custom hook to scroll an element into view,
 * while accounting for a fixed header.
 * Prevents content from being hidden under fixed headers,
 * by scrolling the main content container.
 */
export const useScrollIntoViewWithHeader = ({
  elementRef,
  element,
  shouldScroll,
  headerHeight = 64,
  padding = 10,
  containerSelector = '.h-screen.overflow-y-auto'
}: UseScrollIntoViewWithHeaderOptions) => {
  useEffect(() => {
    if (!shouldScroll) return;

    const targetElement = elementRef?.current || element;

    if (!targetElement) return;

    const elementRect = targetElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Check if element goes below viewport
    if (elementRect.bottom > viewportHeight) {
      // Calculate how much we need to scroll
      const scrollAmount = elementRect.bottom - viewportHeight + padding;

      // Get the current scroll position of the main content container
      const mainContentContainer = document.querySelector(
        containerSelector
      ) as HTMLElement;

      if (mainContentContainer) {
        // Scroll the main content container instead of the entire window
        mainContentContainer.scrollBy({
          top: scrollAmount,
          behavior: 'smooth'
        });
      } else {
        // Fallback: scroll window but account for header
        window.scrollBy({
          top: scrollAmount + headerHeight,
          behavior: 'smooth'
        });
      }
    }
  }, [
    shouldScroll,
    elementRef,
    element,
    headerHeight,
    padding,
    containerSelector
  ]);
};

export default useScrollIntoViewWithHeader;
